import { lazyRouteComponent } from "@tanstack/react-router";
import { Compass, Inbox, UserCheck } from "lucide-react";
import { lazy, Suspense } from "react";

import type { PageDef } from "@/app/registry";

import { parseExploreSearch } from "./search";
import type { FeedSearch, FeedTab } from "./FeedPage";

const TABS: FeedTab[] = ["calls", "following", "channels", "ai"];

const LazyFeedBadge = lazy(() =>
  import("./FeedPage").then((m) => ({ default: m.FeedBadge })),
);

/** Unread calls next to "My Feed" in the menus and the Research Analysts tabs. */
function FeedNavBadge() {
  return (
    <Suspense fallback={null}>
      <LazyFeedBadge />
    </Suspense>
  );
}

function pick<T extends string>(
  v: unknown,
  allowed: readonly T[],
): T | undefined {
  return allowed.includes(v as T) ? (v as T) : undefined;
}

/** Investor side of RA calls: the feed, Explore and RA pages. */
export const pages: PageDef[] = [
  {
    path: "/app/feed",
    title: "My Feed",
    component: lazyRouteComponent(() => import("./FeedPage"), "FeedPage"),
    // the calls of the RAs a member follows or is a client of (V063: open again).
    // Investors' menu only: an RA has his own RA desk (owner, 26 Oct 2026).
    nav: {
      label: "My Feed",
      icon: Inbox,
      group: "Analysts",
      order: 10,
      badge: FeedNavBadge,
    },
    roles: ["admin", "investor"],
    validateSearch: (s): FeedSearch => ({
      tab: TABS.includes(s.tab as FeedTab) ? (s.tab as FeedTab) : undefined,
      status: pick(s.status, ["open", "pending", "closed"] as const),
      side: pick(s.side, ["BUY", "SELL"] as const),
      seg: pick(s.seg, ["equity", "futures", "options"] as const),
      call: Number.isInteger(Number(s.call)) && Number(s.call) > 0 ? Number(s.call) : undefined,
    }),
  },
  // My Feed's two other tabs, as pages under Research Analysts (owner, 29 Oct 2026)
  {
    path: "/app/feed/following",
    title: "Following & invitations",
    component: lazyRouteComponent(() => import("./FeedPage"), "FollowingPage"),
    nav: {
      label: "Following & invitations",
      icon: UserCheck,
      group: "Analysts",
      order: 11,
    },
    roles: ["admin", "investor"],
  },
  // the old Delivery channels page: a redirect to Account → Notifications (1 Nov 2026)
  {
    path: "/app/feed/channels",
    title: "Notifications",
    component: lazyRouteComponent(() => import("./FeedPage"), "ChannelsPage"),
  },
  {
    path: "/app/explore",
    proOnly: true,
    title: "Explore calls",
    component: lazyRouteComponent(() => import("./ExplorePage"), "ExplorePage"),
    nav: {
      label: "Explore calls",
      icon: Compass,
      group: "Analysts",
      order: 20,
    },
    roles: ["admin", "investor"],
    validateSearch: parseExploreSearch,
  },
  {
    path: "/app/explore/ra/$raId",
    title: "Research Analyst",
    component: lazyRouteComponent(
      () => import("./RaPublicPage"),
      "RaPublicPage",
    ),
  },
];
